import dayjs from "dayjs";
import { type DocumentSummary } from "~/api/types";
import { DocumentStatus } from "~/config/types";
import { tierIdToFormValue } from "~/helpers/tier";

export const FilterOperatorEnum = {
  gte: "gte",
  lte: "lte",
  eq: "eq",
  in: "in",
  isNull: "isNull",
} as const;

export type FilterOperator =
  (typeof FilterOperatorEnum)[keyof typeof FilterOperatorEnum];

export const filterFields = [
  "publishedAt",
  "adoptedAt",
  "archiveState",
  "tier",
  "category",
  "tag",
  "owner",
  "publisher",
  "reviewFrom",
  "reviewTo",
] as const;

export type FilterField = (typeof filterFields)[number];

export const dateFilterKeys = [
  "publishedAt",
  "adoptedAt",
  "reviewFrom",
  "reviewTo",
] as const;

export type DateFilterKey = (typeof dateFilterKeys)[number];

export const nullableDateFilterKeys = [
  "adoptedAt",
  "reviewFrom",
  "reviewTo",
] as const;

export type NullableDateFilterKey = (typeof nullableDateFilterKeys)[number];

export const isNullableDateFilterKey = (
  field: FilterField,
): field is NullableDateFilterKey =>
  (nullableDateFilterKeys as readonly string[]).includes(field);

export const multiSelectFilterKeys = [
  "archiveState",
  "tier",
  "category",
  "tag",
  "owner",
  "publisher",
] as const;

export type MultiSelectFilterKey = (typeof multiSelectFilterKeys)[number];

export const filterIsDateByField = (
  field: FilterField,
): field is DateFilterKey =>
  (dateFilterKeys as readonly string[]).includes(field);

export type FilterCondition = {
  field: FilterField;
  operator: FilterOperator;
  value: string | string[];
};

export interface SelectFieldData {
  values: string[];
  labelsByValue: Record<string, string>;
}

export const toDateKey = (value: string | Date) => {
  const date = dayjs(value);
  if (!date.isValid()) return undefined;
  return date.format("YYYY-MM-DD");
};

export const fromDateKey = (dateKey?: string) => {
  if (!dateKey) return undefined;
  const date = new Date(dateKey);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const dateParamKeys: Record<DateFilterKey, { after: string; before: string }> =
  {
    publishedAt: { after: "publishedAfter", before: "publishedBefore" },
    adoptedAt: { after: "adoptedAfter", before: "adoptedBefore" },
    reviewFrom: { after: "reviewFromAfter", before: "reviewFromBefore" },
    reviewTo: { after: "reviewToAfter", before: "reviewToBefore" },
  };

const evaluateFilter = (
  rowValue: string,
  filter: Pick<FilterCondition, "operator" | "value">,
): boolean => {
  if (filter.operator === "in") {
    if (!Array.isArray(filter.value)) {
      throw new Error("Expected array value for in filter");
    }
    return filter.value.includes(rowValue);
  }

  if (typeof filter.value !== "string") {
    throw new Error(`Expected string value for ${filter.operator} filter`);
  }

  if (filter.operator === "gte") {
    return rowValue >= filter.value;
  }
  if (filter.operator === "lte") {
    return rowValue <= filter.value;
  }
  if (filter.operator === "eq") {
    return rowValue === filter.value;
  }

  throw new Error(`Unsupported filter operator: ${filter.operator}`);
};

interface ParseFiltersOptions {
  multiSelectFields?: readonly MultiSelectFilterKey[];
  dateFields?: readonly DateFilterKey[];
}

export const parseFilters = (
  searchParams: URLSearchParams,
  options: ParseFiltersOptions = {},
): FilterCondition[] => {
  const {
    multiSelectFields = multiSelectFilterKeys,
    dateFields = dateFilterKeys,
  } = options;
  const filters: FilterCondition[] = [];

  for (const field of multiSelectFields) {
    const values = searchParams
      .getAll(field)
      .filter((value) => isFilterFieldValueValid(field, value));
    if (values.length === 0) continue;

    filters.push(
      values.length === 1
        ? { field, operator: FilterOperatorEnum.eq, value: values[0]! }
        : { field, operator: FilterOperatorEnum.in, value: values },
    );
  }

  for (const field of dateFields) {
    const paramKeys = dateParamKeys[field];

    const afterValue = searchParams.get(paramKeys.after);
    if (afterValue && toDateKey(afterValue) !== undefined) {
      filters.push({
        field,
        operator: FilterOperatorEnum.gte,
        value: afterValue,
      });
    }

    const beforeValue = searchParams.get(paramKeys.before);
    if (beforeValue && toDateKey(beforeValue) !== undefined) {
      filters.push({
        field,
        operator: FilterOperatorEnum.lte,
        value: beforeValue,
      });
    }
  }

  const nullableDateFields = dateFields.filter(isNullableDateFilterKey);
  for (const field of nullableDateFields) {
    if (searchParams.get(field) === "null") {
      filters.push({
        field,
        operator: FilterOperatorEnum.isNull,
        value: "null",
      });
    }
  }

  return filters;
};

export const buildFilterParamKeysForField = (field: FilterField): string[] => {
  if (filterIsDateByField(field)) {
    const paramKeys = dateParamKeys[field];
    const keys = [paramKeys.after, paramKeys.before];
    if (isNullableDateFilterKey(field)) {
      keys.push(field);
    }
    return keys;
  }
  return [field];
};

export const buildSearchParamsFromFilters = (
  filters: FilterCondition[],
): URLSearchParams => {
  const params = new URLSearchParams();

  for (const filter of filters) {
    if (filter.operator === FilterOperatorEnum.isNull) {
      params.set(filter.field, "null");
    } else if (filterIsDateByField(filter.field)) {
      if (typeof filter.value !== "string" || filter.value.length === 0) {
        continue;
      }
      const paramKeys = dateParamKeys[filter.field];
      const paramKey =
        filter.operator === FilterOperatorEnum.gte
          ? paramKeys.after
          : paramKeys.before;
      params.set(paramKey, filter.value);
    } else {
      const values = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];
      for (const value of values) {
        if (value.length > 0) {
          params.append(filter.field, value);
        }
      }
    }
  }

  return params;
};

export const isFilterFieldValueValid = (field: FilterField, value: string) => {
  if (value === "null" && isNullableDateFilterKey(field)) {
    return true;
  }
  if (filterIsDateByField(field)) {
    return toDateKey(value) !== undefined;
  }
  return value.trim().length > 0;
};

const getDocumentFilterValue = (
  document: DocumentSummary,
  field: FilterField,
): string | string[] | null => {
  switch (field) {
    case "publishedAt":
      return document.latestPublishedRelease?.artifact.publishedAt ?? null;
    case "adoptedAt":
      return document.latestPublishedRelease?.adoptedAt ?? null;
    case "archiveState":
      return document.archivedAt === null
        ? DocumentStatus.active
        : DocumentStatus.archived;
    case "tier":
      return tierIdToFormValue(document.tier?.id);
    case "category":
      return document.category?.id.toString() ?? [];
    case "tag":
      return document.tags.map((tag) => tag.id.toString());
    case "owner":
      return document.owner.userId.toString();
    case "publisher":
      return document.publisher?.userId.toString() ?? null;
    case "reviewFrom":
      return document.latestRelease.artifact.reviewFrom;
    case "reviewTo":
      return document.latestRelease.artifact.reviewTo;
  }
};

const matchesDocumentFilter = (
  document: DocumentSummary,
  filter: FilterCondition,
) => {
  const fieldValue = getDocumentFilterValue(document, filter.field);

  if (filter.field === "tag") {
    if (!Array.isArray(fieldValue)) {
      return false;
    }
    if (filter.operator === "eq" && typeof filter.value === "string") {
      return fieldValue.includes(filter.value);
    }
    if (filter.operator === "in" && Array.isArray(filter.value)) {
      return fieldValue.some((value) => filter.value.includes(value));
    }
    throw new Error(`Unsupported tag filter operator: ${filter.operator}`);
  }

  if (filter.operator === FilterOperatorEnum.isNull) {
    return fieldValue === null;
  }

  if (filterIsDateByField(filter.field)) {
    if (typeof fieldValue !== "string" || typeof filter.value !== "string") {
      return false;
    }
    const dateValue = toDateKey(fieldValue);
    if (!dateValue) {
      return false;
    }
    return evaluateFilter(dateValue, {
      operator: filter.operator,
      value: filter.value,
    });
  }

  if (typeof fieldValue !== "string") {
    return false;
  }

  return evaluateFilter(fieldValue, {
    operator: filter.operator,
    value: filter.value,
  });
};

export const filterDocuments = (
  documents: DocumentSummary[],
  filters: FilterCondition[],
) => {
  const hasArchiveStateFilter = filters.some(
    (fc) => fc.field === "archiveState",
  );
  const effectiveFilters: FilterCondition[] = hasArchiveStateFilter
    ? filters
    : [
        ...filters,
        {
          field: "archiveState",
          operator: FilterOperatorEnum.eq,
          value: DocumentStatus.active,
        },
      ];

  return documents.filter((row) =>
    effectiveFilters.every((filter) => matchesDocumentFilter(row, filter)),
  );
};
